/**
 * `CodModalidadePeriodoGarantido` do backend — `livance-system`,
 * `Livance.InfraEstruturaCore/Models/Enums/CodModalidadePeriodoGarantido.cs`.
 *
 * As sete existem em produção e todas aparecem em Períodos Garantidos já criados — remover um valor
 * daqui quebraria a leitura deles.
 */
export enum ReservedScheduleTypeEnum {
  Livance = 1,
  Porto = 2,
  Psico = 3,
  BizDev = 4,
  Careplus = 5,
  Sami = 6,
  Saludia = 7,
}

const RESERVED_SCHEDULE_TYPES = Object.values(ReservedScheduleTypeEnum).filter(
  (value): value is ReservedScheduleTypeEnum => typeof value === "number",
);

/**
 * O número é uma das sete modalidades que o backend conhece?
 *
 * Existe porque a flag `modalidade-periodo-garantido-por-usuario` devolve um `number` cru: fora
 * deste conjunto não há termo de uso correspondente, e o backend lança nesse caso. Ver
 * `resolveReservedScheduleModality`.
 */
export const isReservedScheduleType = (
  value: number,
): value is ReservedScheduleTypeEnum => {
  return RESERVED_SCHEDULE_TYPES.some((known) => known.valueOf() === value);
};

/**
 * Modalidades **descontinuadas**: seguem válidas para ler os PGs já criados, mas nunca podem ser
 * ofertadas na criação de um novo — nem no campo de categoria, nem em qualquer composição que
 * alimente uma escolha.
 *
 * A exclusão é **explícita de propósito**. Hoje a Psico também sumiria por dois acidentes: nenhum
 * parceiro tem a coluna `codModalidadePeriodoGarantido` preenchida com ela, e ela não tem link de
 * termo publicado. Os dois são fatos do dado e podem mudar sem ninguém associar a mudança a esta
 * regra — no dia em que mudassem, a Psico voltaria a ser ofertável em silêncio.
 */
export const DISCONTINUED_RESERVED_SCHEDULE_TYPES = [
  ReservedScheduleTypeEnum.Psico,
] as const;

/** Modalidades que podem ser escolhidas na criação de um PG novo. */
export type SelectableReservedScheduleType = Exclude<
  ReservedScheduleTypeEnum,
  (typeof DISCONTINUED_RESERVED_SCHEDULE_TYPES)[number]
>;

/**
 * Checa as duas condições, e as duas importam: um valor fora do enum não é selecionável **nem**
 * conhecido, então afirmar `SelectableReservedScheduleType` só por ele não ser descontinuado seria
 * mentir para o compilador.
 */
export const isSelectableReservedScheduleType = (
  reservedScheduleType: number,
): reservedScheduleType is SelectableReservedScheduleType => {
  return (
    isReservedScheduleType(reservedScheduleType) &&
    !DISCONTINUED_RESERVED_SCHEDULE_TYPES.some(
      (discontinued) => discontinued.valueOf() === reservedScheduleType,
    )
  );
};
